import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
  orderBy,
  limit as firestoreLimit,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { ExpenseService } from './expense.service';
import {
  Expense,
  ExpenseCategory,
  ExpenseStatus,
  PaymentMethod,
  RecurrenceFrequency,
} from '@/models/expense.model';

/**
 * Billing Transaction to Expense Service
 * 
 * This service pulls billing transactions from Firestore and converts them
 * to expense records in the operations portal.
 */

interface BillingTransaction {
  id: string;
  type: number; // 0=employerMonthlySubscription, 1=employerClaimFee, 2=providerTransactionFee, 3=providerMonthlyFee
  employerId?: string;
  providerId?: string;
  receiptId?: string;
  visitId?: string;
  amount: number;
  description: string;
  employeeCount?: number;
  invoiceId?: string;
  status: number; // 0=pending, 1=invoiced, 2=paid, 3=cancelled
  transactionDate: Timestamp;
  invoicedAt?: Timestamp;
  metadata?: Record<string, unknown>;
  createdAt: Timestamp;
  lastUpdated: Timestamp;
}

export class BillingToExpenseService {
  private static readonly BILLING_TRANSACTIONS_COLLECTION = 'billingTransactions';
  private static readonly DEFAULT_EMPLOYEE_ID = 'system';
  private static readonly DEFAULT_EMPLOYEE_NAME = 'System';
  private static readonly DEFAULT_EMPLOYEE_EMAIL = 'system@faircare.com';

  /**
   * Map billing transaction type to expense category
   */
  private static mapTypeToCategory(type: number): ExpenseCategory {
    switch (type) {
      case 0: // employerMonthlySubscription
        return ExpenseCategory.saasSubscriptions;
      case 1: // employerClaimFee
        return ExpenseCategory.professional;
      case 2: // providerTransactionFee
        return ExpenseCategory.bankFees;
      case 3: // providerMonthlyFee
        return ExpenseCategory.saasSubscriptions;
      default:
        return ExpenseCategory.other;
    }
  }

  /**
   * Map billing transaction type to subcategory
   */
  private static mapTypeToSubcategory(type: number): string {
    switch (type) {
      case 0:
        return 'Employer Monthly Subscription';
      case 1:
        return 'Claim Processing Fee';
      case 2:
        return 'Payment Processing Fee';
      case 3:
        return 'Provider Monthly Fee';
      default:
        return 'Billing Transaction';
    }
  }

  /**
   * Map billing transaction status to expense status
   */
  private static mapStatusToExpenseStatus(status: number): ExpenseStatus {
    switch (status) {
      case 0: // pending
        return ExpenseStatus.submitted;
      case 1: // invoiced
        return ExpenseStatus.submitted;
      case 2: // paid
        return ExpenseStatus.approved;
      case 3: // cancelled
        return ExpenseStatus.rejected;
      default:
        return ExpenseStatus.submitted;
    }
  }

  /**
   * Convert billing transaction to expense data
   */
  private static billingTransactionToExpense(
    transaction: BillingTransaction
  ): Partial<Expense> {
    const transactionDate = transaction.transactionDate.toDate();
    const category = this.mapTypeToCategory(transaction.type);
    const subcategory = this.mapTypeToSubcategory(transaction.type);
    const status = this.mapStatusToExpenseStatus(transaction.status);

    // Build description with context
    let description = transaction.description;
    if (transaction.employerId) {
      description += ` (Employer: ${transaction.employerId})`;
    }
    if (transaction.providerId) {
      description += ` (Provider: ${transaction.providerId})`;
    }
    if (transaction.employeeCount) {
      description += ` - ${transaction.employeeCount} employee(s)`;
    }

    // Determine if this is recurring (monthly subscriptions are recurring)
    const isRecurring = transaction.type === 0 || transaction.type === 3;
    
    // Build custom fields to track the source billing transaction
    const customFields: Record<string, unknown> = {
      billingTransactionId: transaction.id,
      billingTransactionType: transaction.type,
      originalStatus: transaction.status,
    };
    
    if (transaction.employerId) {
      customFields.employerId = transaction.employerId;
    }
    if (transaction.providerId) {
      customFields.providerId = transaction.providerId;
    }
    if (transaction.receiptId) {
      customFields.receiptId = transaction.receiptId;
    }
    if (transaction.visitId) {
      customFields.visitId = transaction.visitId;
    }
    if (transaction.invoiceId) {
      customFields.invoiceId = transaction.invoiceId;
    }
    if (transaction.metadata) {
      customFields.originalMetadata = transaction.metadata;
    }

    const expenseData: Partial<Expense> = {
      employeeId: this.DEFAULT_EMPLOYEE_ID,
      employeeName: this.DEFAULT_EMPLOYEE_NAME,
      employeeEmail: this.DEFAULT_EMPLOYEE_EMAIL,
      category,
      subcategory,
      description,
      amount: transaction.amount,
      currency: 'USD',
      taxDeductible: true,
      expenseDate: transactionDate,
      paymentMethod: PaymentMethod.invoice,
      receiptRequired: false,
      receiptVerified: transaction.status === 2, // Verified if paid
      status,
      billable: false,
      isRecurring,
      notes: `Converted from billing transaction ${transaction.id}`,
      internalNotes: `Billing transaction type: ${transaction.type}, Status: ${transaction.status}`,
      tags: ['billing-transaction', 'auto-converted'],
      customFields,
      createdBy: 'system',
    };

    // Add recurrence info for monthly subscriptions
    if (isRecurring) {
      const nextMonth = new Date(transactionDate);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      
      expenseData.recurrence = {
        frequency: RecurrenceFrequency.monthly,
        startDate: transactionDate,
        nextOccurrence: nextMonth,
      };
    }

    // If transaction is paid, mark expense as approved and paid
    if (transaction.status === 2) {
      expenseData.approvedBy = 'system';
      expenseData.approvedByName = 'System';
      expenseData.approvedAt = transaction.invoicedAt?.toDate() || transactionDate;
      expenseData.paidAt = transaction.invoicedAt?.toDate() || transactionDate;
      expenseData.paidBy = 'system';
    }

    return expenseData;
  }

  /**
   * Get billing transactions from Firestore
   */
  static async getBillingTransactions(options?: {
    status?: number; // Filter by status
    startDate?: Date; // Filter by date range
    endDate?: Date;
    limit?: number;
    employerId?: string;
    providerId?: string;
  }): Promise<BillingTransaction[]> {
    const constraints: Array<ReturnType<typeof where> | ReturnType<typeof orderBy> | ReturnType<typeof firestoreLimit>> = [];

    // Add status filter
    if (options?.status !== undefined) {
      constraints.push(where('status', '==', options.status));
    }

    // Add date range filters
    if (options?.startDate) {
      constraints.push(
        where('transactionDate', '>=', Timestamp.fromDate(options.startDate))
      );
    }
    if (options?.endDate) {
      constraints.push(
        where('transactionDate', '<=', Timestamp.fromDate(options.endDate))
      );
    }

    // Add employer filter
    if (options?.employerId) {
      constraints.push(where('employerId', '==', options.employerId));
    }

    // Add provider filter
    if (options?.providerId) {
      constraints.push(where('providerId', '==', options.providerId));
    }

    // Order by transaction date (newest first)
    constraints.push(orderBy('transactionDate', 'desc'));

    // Add limit if specified
    if (options?.limit) {
      constraints.push(firestoreLimit(options.limit));
    }

    const q = query(
      collection(db, this.BILLING_TRANSACTIONS_COLLECTION),
      ...constraints
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as Omit<BillingTransaction, 'id'>),
    }));
  }

  /**
   * Check if an expense already exists for a billing transaction
   */
  static async expenseExistsForTransaction(
    transactionId: string
  ): Promise<boolean> {
    const expenses = await ExpenseService.getExpensesByDateRange(
      new Date(0), // Start from epoch
      new Date() // To now
    );

    return expenses.some(
      (expense) =>
        expense.customFields?.billingTransactionId === transactionId
    );
  }

  /**
   * Convert billing transactions to expenses
   */
  static async convertBillingTransactionsToExpenses(options?: {
    status?: number;
    startDate?: Date;
    endDate?: Date;
    limit?: number;
    employerId?: string;
    providerId?: string;
    skipExisting?: boolean; // Skip transactions that already have expenses
  }): Promise<{
    success: boolean;
    converted: number;
    skipped: number;
    expenseIds: string[];
    errors: string[];
  }> {
    const errors: string[] = [];
    const expenseIds: string[] = [];
    let skipped = 0;

    try {
      // Get billing transactions
      const transactions = await this.getBillingTransactions({
        status: options?.status,
        startDate: options?.startDate,
        endDate: options?.endDate,
        limit: options?.limit,
        employerId: options?.employerId,
        providerId: options?.providerId,
      });

      if (transactions.length === 0) {
        return {
          success: true,
          converted: 0,
          skipped: 0,
          expenseIds: [],
          errors: ['No billing transactions found'],
        };
      }

      // Convert each transaction to an expense
      for (const transaction of transactions) {
        try {
          // Skip if expense already exists
          if (options?.skipExisting !== false) {
            const exists = await this.expenseExistsForTransaction(transaction.id);
            if (exists) {
              skipped++;
              continue;
            }
          }

          // Convert to expense
          const expenseData = this.billingTransactionToExpense(transaction);
          const expenseId = await ExpenseService.createExpense(expenseData);
          expenseIds.push(expenseId);
        } catch (error) {
          const errorMsg = `Failed to convert transaction ${transaction.id}: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`;
          errors.push(errorMsg);
          console.error(errorMsg, error);
        }
      }

      return {
        success: errors.length === 0,
        converted: expenseIds.length,
        skipped,
        expenseIds,
        errors,
      };
    } catch (error) {
      const errorMsg = `Failed to convert billing transactions: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`;
      errors.push(errorMsg);
      console.error(errorMsg, error);

      return {
        success: false,
        converted: expenseIds.length,
        skipped,
        expenseIds,
        errors,
      };
    }
  }
}
